import { ref, onUnmounted } from 'vue';
import { readPublicMusicList } from '~/utils/public-music-list.mjs';

/** Independent public section loading; never apply another locale's late result. */
export function useHomeMusicSection<T>(
  fetchSection: () => Promise<unknown>,
  currentKey: () => string,
  initialItems?: T[],
) {
  const list = ref(initialItems ?? []) as { value: T[] };
  const isLoading = ref(initialItems === undefined);
  const error = ref(false);
  const loadedKey = ref(initialItems === undefined ? '' : currentKey());
  let requestVersion = 0;
  let pendingKey: string | null = null;
  let disposed = false;

  const load = async () => {
    const key = currentKey();
    if (disposed || pendingKey === key) return;
    const request = ++requestVersion;
    pendingKey = key;
    isLoading.value = true;
    error.value = false;
    try {
      const response = await fetchSection();
      if (disposed || request !== requestVersion || key !== currentKey()) return;
      list.value = readPublicMusicList(response);
      loadedKey.value = key;
    } catch {
      if (disposed || request !== requestVersion || key !== currentKey()) return;
      list.value = [];
      loadedKey.value = '';
      error.value = true;
    } finally {
      if (!disposed && request === requestVersion) {
        pendingKey = null;
        isLoading.value = false;
      }
    }
  };

  onUnmounted(() => { disposed = true; requestVersion++; });
  return { list, isLoading, error, loadedKey, load };
}
